import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';

// NOTE: Context split into getter and setter so updates don't trigger unnecessary re-renders of descendant subscribers.
type StepperActiveIndexContextType = number;
const ActiveStepIndexContext = createContext<StepperActiveIndexContextType | null>(null);

type StepperIndexContextType = {
  activeStepIndex: number;
  previousStepIndex: number;
};
const StepperIndexContext = createContext<StepperIndexContextType | null>(null);

export type SetStepperActiveIndexContextType = {
  onNext: (index?: number) => void;
  onEdit: (index: number) => void;
};

const SetActiveStepIndexContext = createContext<SetStepperActiveIndexContextType | null>(null);

export const StepperProvider = ({ children }: { children: ReactNode }) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [previousStepIndex, setPreviousStepIndex] = useState<number>(0);

  const value = {
    onNext: (index?: number) => {
      if (index !== undefined) {
        setActiveStepIndex(index);
      } else if (previousStepIndex) {
        setActiveStepIndex(previousStepIndex);
      } else {
        setActiveStepIndex(prev => prev + 1);
      }
      setPreviousStepIndex(0);
    },
    onEdit: (index: number) => {
      setPreviousStepIndex(activeStepIndex);
      setActiveStepIndex(index);
    },
  };

  return (
    <SetActiveStepIndexContext.Provider value={value}>
      <ActiveStepIndexContext.Provider value={activeStepIndex}>
        <StepperIndexContext.Provider value={{ activeStepIndex, previousStepIndex }}>
          {children}
        </StepperIndexContext.Provider>
      </ActiveStepIndexContext.Provider>
    </SetActiveStepIndexContext.Provider>
  );
};

export const useStepperActiveIndex = () => {
  const context = useContext(ActiveStepIndexContext);
  if (context === null) {
    throw new Error('useStepperActiveIndex must be used within a StepperProvider');
  }

  return context;
};

export const useStepperIndex = () => {
  const context = useContext(StepperIndexContext);
  if (context === null) {
    throw new Error('useStepperIndex must be used within a StepperProvider');
  }

  return context;
};

export const useStepperActions = () => {
  const context = useContext(SetActiveStepIndexContext);

  if (!context) {
    throw new Error('useStepperActions must be used within a StepperProvider');
  }

  return context;
};
